import { z } from 'zod';
import { localeSchema } from './auth.js';

export const MAX_CHAT_TEXT_LENGTH = 4000;
export const CHAT_PAGE_SIZE = 50;

/** A dictated message: at most 2 minutes, sent as 16 kHz mono WAV. */
export const MAX_DICTATION_SECONDS = 120;
export const DICTATION_SAMPLE_RATE = 16_000;
export const MAX_DICTATION_BYTES = MAX_DICTATION_SECONDS * DICTATION_SAMPLE_RATE * 2 + 1024;
export const dictationResponseSchema = z.object({ text: z.string() });
export type DictationResponse = z.infer<typeof dictationResponseSchema>;

export const chatAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  /** Shown inline; everything else is a download. */
  image: z.boolean(),
  url: z.string(),
});
export type ChatAttachment = z.infer<typeof chatAttachmentSchema>;

const vec3 = z.tuple([z.number(), z.number(), z.number()]);

/** Part of the 2D plan: its middle and extent in model metres, on one floor. */
export const planSectionSchema = z.object({
  center: z.object({ x: z.number(), y: z.number() }),
  width: z.number().positive(),
  height: z.number().positive(),
  level: z.number().int().min(0).max(50),
});
export type PlanSection = z.infer<typeof planSectionSchema>;

/** Eye of somebody walking through the model (three.js coordinates), heading and pitch. */
export const walkerPoseSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
  yaw: z.number(),
  pitch: z.number(),
});

/**
 * Where a shared picture was taken: the 3D camera (three.js coordinates) or a plan section.
 * Taken while walking, `walker` puts whoever opens it at the same spot (older clients ignore
 * it and get the camera).
 */
export const chatViewSchema = z.union([
  z.object({
    mode: z.literal('3d').optional(),
    position: vec3,
    target: vec3,
    walker: walkerPoseSchema.optional(),
  }),
  z.object({ mode: z.literal('2d'), plan: planSectionSchema }),
]);
export type ChatView = z.infer<typeof chatViewSchema>;

export const callUtteranceSchema = z.object({
  id: z.string(),
  speakerId: z.string().nullable(),
  speakerName: z.string(),
  language: z.string(),
  text: z.string(),
  translations: z.record(z.string(), z.string()),
  spokenAt: z.string(),
});
export type CallUtterance = z.infer<typeof callUtteranceSchema>;

export const chatMessageSchema = z.object({
  id: z.string(),
  kind: z.enum(['text', 'file', 'view', 'call']),
  authorId: z.string().nullable(),
  authorName: z.string(),
  text: z.string(),
  /** Detected language of `text`; null while not yet translated or without translation. */
  language: z.string().nullable(),
  /** Machine translations by language code. */
  translations: z.record(z.string(), z.string()),
  attachments: z.array(chatAttachmentSchema),
  /** Kind `view`: where the picture was taken. */
  view: chatViewSchema.nullable(),
  /** Kind `call`: when it ran and who took part, with what was said. */
  call: z
    .object({
      startedAt: z.string(),
      endedAt: z.string().nullable(),
      participants: z.array(z.string()),
      utterances: z.array(callUtteranceSchema),
    })
    .nullable(),
  createdAt: z.string(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const chatListResponseSchema = z.object({
  /** Oldest first. */
  messages: z.array(chatMessageSchema),
  /** Older messages exist before the first one returned. */
  hasMore: z.boolean(),
  /** Messages of others after `lastReadAt`. */
  unread: z.number().int(),
  lastReadAt: z.string().nullable(),
});
export type ChatListResponse = z.infer<typeof chatListResponseSchema>;

export const chatMessageResponseSchema = z.object({ message: chatMessageSchema });

export const sendChatRequestSchema = z.object({
  text: z.string().trim().min(1).max(MAX_CHAT_TEXT_LENGTH),
});
export type SendChatRequest = z.infer<typeof sendChatRequestSchema>;

export const chatReadRequestSchema = z.object({ at: z.iso.datetime() });

/* ------------------------------------------------------------------------- voice call */

/** Services that interpret a call; without any, the original voices are passed on. */
export const CALL_PROVIDERS = ['gemini', 'deepl'] as const;
export const callProviderSchema = z.enum(CALL_PROVIDERS);
export type CallProvider = z.infer<typeof callProviderSchema>;

export const iceServerSchema = z.object({
  urls: z.union([z.string(), z.array(z.string())]),
  username: z.string().optional(),
  credential: z.string().optional(),
});

/** Project members who have InduSketch open right now (anywhere in the app). */
export const projectPresenceResponseSchema = z.object({ online: z.array(z.string()) });

export const callOptionsResponseSchema = z.object({
  /** Interpreters with a key configured on the server, best first. */
  providers: z.array(callProviderSchema),
  iceServers: z.array(iceServerSchema),
});
export type CallOptionsResponse = z.infer<typeof callOptionsResponseSchema>;

export const interpreterRequestSchema = z.object({
  provider: callProviderSchema,
  speaks: localeSchema,
  hears: localeSchema,
});
export type InterpreterRequest = z.infer<typeof interpreterRequestSchema>;

/** Short-lived credentials the browser uses to talk to the interpreter directly. */
export const interpreterSessionSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('gemini'),
    url: z.string(),
    token: z.string(),
    model: z.string(),
  }),
  z.object({
    provider: z.literal('deepl'),
    url: z.string(),
    token: z.string(),
  }),
]);
export type InterpreterSession = z.infer<typeof interpreterSessionSchema>;

export const callMemberSchema = z.object({
  peerId: z.string(),
  userId: z.string(),
  name: z.string(),
  speaks: localeSchema,
  hears: localeSchema,
  /** Hear the original voice quietly under the translation. */
  original: z.boolean(),
  muted: z.boolean(),
  /** Interpreter of what this member says (missing from older clients). */
  provider: callProviderSchema.nullable().optional(),
});
export type CallMember = z.infer<typeof callMemberSchema>;

export interface CallState {
  /** Chat message the call's protocol is written to. */
  readonly messageId: string;
  readonly startedAt: string;
  readonly members: readonly CallMember[];
  /**
   * Interpreter for everybody in the call: chosen by who started it, any member may switch
   * (null: started by an older client, each member uses the own choice).
   */
  readonly provider?: CallProvider | null;
  /** Who chose the interpreter last (peer id and name), for the notice to the others. */
  readonly providerBy?: { readonly peerId: string; readonly name: string } | null;
}

/** WebRTC handshake data relayed between two peers of a call. */
export const rtcSignalSchema = z.union([
  z.object({
    description: z.object({
      type: z.enum(['offer', 'answer']),
      sdp: z.string().max(64 * 1024),
    }),
  }),
  z.object({
    candidate: z.object({
      candidate: z.string().max(2048),
      sdpMid: z.string().max(64).nullable().optional(),
      sdpMLineIndex: z.number().int().nullable().optional(),
      usernameFragment: z.string().max(256).nullable().optional(),
    }),
  }),
]);
export type RtcSignal = z.infer<typeof rtcSignalSchema>;

export const spokenUtteranceSchema = z.object({
  language: localeSchema,
  text: z.string().trim().min(1).max(MAX_CHAT_TEXT_LENGTH),
  translations: z.partialRecord(localeSchema, z.string().max(MAX_CHAT_TEXT_LENGTH)),
});
export type SpokenUtterance = z.infer<typeof spokenUtteranceSchema>;

export const callActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('join'),
    speaks: localeSchema,
    hears: localeSchema,
    original: z.boolean(),
    provider: callProviderSchema.nullable().optional(),
    /** Starting the call: ring only these users (otherwise every project member). */
    ringTo: z.array(z.uuid()).max(50).optional(),
  }),
  z.object({ action: z.literal('leave') }),
  z.object({
    action: z.literal('update'),
    speaks: localeSchema,
    hears: localeSchema,
    original: z.boolean(),
    muted: z.boolean(),
    provider: callProviderSchema.nullable().optional(),
  }),
  z.object({ action: z.literal('utterance'), utterance: spokenUtteranceSchema }),
  /** A member switches the interpreter for the whole call. */
  z.object({ action: z.literal('provider'), provider: callProviderSchema }),
  /** A member rings the others in the project once more (or only `userIds`). */
  z.object({ action: z.literal('ring'), userIds: z.array(z.uuid()).max(50).optional() }),
  /** Somebody who was rung does not want to join. */
  z.object({ action: z.literal('decline') }),
]);
export type CallAction = z.infer<typeof callActionSchema>;
